// Advanced Search Modal - Comprehensive structure building interface
// Provides visual structure builder, sugar palette, and configuration options

import { useState, useEffect } from 'react';
import { Modal, Button, Icon, Tab, Dropdown, Label } from 'semantic-ui-react';
import PatternCanvas from './search/PatternCanvas';
import SugarPalette from './search/SugarPalette';
import SearchConfiguration from './search/SearchConfiguration';
import BranchBuilder from './search/BranchBuilder';
import SNFGsvg from '../visualizer-tool/glycan-interpreter/snfgSVG';
import resdict from '../visualizer-tool/data/resdict.json';
import {
  isMultiModuleQuery,
  parseMultiModuleQuery,
  getModuleColor,
  getModuleIcon
} from '../../utils/queryTokenDetector';
import { detectSearchScopeFromQuery } from '../../utils/searchScopeDetector';
import '../css/advanced-search.css';

export default function AdvancedSearchModal({ open, onClose, onSearch, initialPattern, initialSearchType = 'all' }) {
  // Load config from localStorage with defaults
  const loadConfigFromStorage = () => {
    try {
      const savedConfig = localStorage.getItem('sugardb_search_config');
      if (savedConfig) {
        return JSON.parse(savedConfig);
      }
    } catch (error) {
      console.error('Error loading config from localStorage:', error);
    }

    // Return default config
    return {
      carbuilder: {
        searchScope: 'anywhere',
        stereochemistryMode: 'agnostic',
        enableWildcards: true,
        bondStereochemistry: 'ignore',
        enabledMatchers: {
          mainChain: true,
          exactBranch: true,
          anywhere: true,
          wildcard: true
        }
      }
    };
  };

  // Pattern state
  const [pattern, setPattern] = useState({
    mainChain: [],
    branches: [],
    bonds: [] // Array of bond objects, aligned with mainChain (bond AFTER each sugar)
  });

  // Configuration state with localStorage persistence
  const [config, setConfig] = useState(loadConfigFromStorage());

  // UI state
  const [textQuery, setTextQuery] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [showBranchBuilder, setShowBranchBuilder] = useState(false);
  const [searchType, setSearchType] = useState(initialSearchType);
  const [isMultiModule, setIsMultiModule] = useState(false);
  const [tokenPreview, setTokenPreview] = useState([]);

  // Save config to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('sugardb_search_config', JSON.stringify(config));
    } catch (error) {
      console.error('Error saving config to localStorage:', error);
    }
  }, [config]);

  // Parse initial pattern if provided
  useEffect(() => {
    if (initialPattern && initialPattern.trim()) {
      // Check if this is a multi-module query
      const isMulti = isMultiModuleQuery(initialPattern);
      setIsMultiModule(isMulti);

      if (isMulti) {
        // Multi-module query: parse tokens for preview, skip structure parsing
        const tokens = parseMultiModuleQuery(initialPattern);
        setTokenPreview(tokens);
        setTextQuery(initialPattern);
        // Auto-switch to text query tab for multi-module queries
        setActiveTab(1);
      } else {
        // Single-module query: parse as carbuilder structure
        try {
          const parsedPattern = queryToPattern(initialPattern);
          setPattern(parsedPattern);
          setTextQuery(initialPattern);
          setTokenPreview([]);

          // Auto-detect search scope from query and update config
          const detectedScope = detectSearchScopeFromQuery(initialPattern);
          setConfig(prevConfig => ({
            ...prevConfig,
            carbuilder: {
              ...prevConfig.carbuilder,
              searchScope: detectedScope
            }
          }));
        } catch (error) {
          console.error('Error parsing initial pattern:', error);
          setTextQuery(initialPattern);
          setTokenPreview([]);
        }
      }
    }
  }, [initialPattern]);

  // Sync pattern to text query
  useEffect(() => {
    const query = patternToQuery(pattern);
    setTextQuery(query);
  }, [pattern]);

  // Convert pattern object to query string (including bonds and branches)
  const patternToQuery = (patternObj) => {
    // BRANCH-ONLY MODE: If no main chain but has branches, use the first branch as the pattern
    if ((!patternObj.mainChain || patternObj.mainChain.length === 0) &&
        patternObj.branches && patternObj.branches.length > 0) {
      // In branch-only mode, wrap the branch pattern in brackets for UI consistency
      // This enables auto-detection to work correctly: [GlcA] → scope='branches'
      const firstBranch = patternObj.branches[0];
      const branchPattern = branchToQuery(firstBranch);
      return `[${branchPattern}]`;
    }

    // ANYWHERE/MAINCHAIN MODE: Normal processing
    if (!patternObj.mainChain || patternObj.mainChain.length === 0) {
      return '';
    }

    const parts = [];

    for (let i = 0; i < patternObj.mainChain.length; i++) {
      const sugar = patternObj.mainChain[i];
      const bond = patternObj.bonds && patternObj.bonds[i];

      // Add sugar
      if (sugar.isWildcard) {
        parts.push('*');
      } else {
        parts.push(sugar.sugar);
      }

      // Add branches attached to this sugar (if any)
      if (patternObj.branches && patternObj.branches.length > 0) {
        const branchesAtThisPosition = patternObj.branches.filter(
          branch => branch.attachToPosition === i
        );

        for (const branch of branchesAtThisPosition) {
          const branchQuery = branchToQuery(branch);
          if (branchQuery) {
            parts.push(`[${branchQuery}]`);
          }
        }
      }

      // Add bond if exists (and not the last sugar)
      if (bond && i < patternObj.mainChain.length - 1) {
        const bondNotation = formatBondNotation(bond);
        if (bondNotation) {
          parts.push(bondNotation);
        }
      }
    }

    return parts.join('-');
  };

  // Convert branch object to query string
  const branchToQuery = (branch) => {
    if (!branch.sugars || branch.sugars.length === 0) {
      return '';
    }

    const parts = [];

    for (let i = 0; i < branch.sugars.length; i++) {
      const sugar = branch.sugars[i];
      const bond = branch.bonds && branch.bonds[i];

      // Add sugar
      if (sugar.isWildcard) {
        parts.push('*');
      } else {
        parts.push(sugar.sugar);
      }

      // Add bond if exists (internal bonds within the branch)
      if (bond && i < branch.sugars.length - 1) {
        const bondNotation = formatBondNotation(bond);
        if (bondNotation) {
          parts.push(bondNotation);
        }
      }
    }

    // Add attachment bond at the end (how the branch connects to main chain)
    if (branch.attachmentBond) {
      const attachmentNotation = formatBondNotation(branch.attachmentBond);
      if (attachmentNotation) {
        parts.push(attachmentNotation);
      }
    }

    return parts.join('-');
  };

  // Format bond object to string notation
  const formatBondNotation = (bond) => {
    if (!bond) return null;

    // If anomeric is specified, use shorthand (e.g., "b4", "a3")
    if (bond.anomeric && bond.to) {
      return `${bond.anomeric}${bond.to}`;
    }

    // Otherwise just position (e.g., "4")
    if (bond.to) {
      return `${bond.to}`;
    }

    return null;
  };

  // Normalize query string (same logic as backend query parser)
  const normalizeQueryString = (queryString) => {
    // Sugar word to abbreviation mapping (matching backend)
    const SUGAR_WORD_MAP = {
      'glucose': 'Glc', 'galactose': 'Gal', 'mannose': 'Man',
      'allose': 'All', 'altrose': 'Alt', 'gulose': 'Gul',
      'idose': 'Ido', 'talose': 'Tal', 'fucose': 'Fuc',
      'rhamnose': 'Rha', 'quinovose': 'Qui', 'arabinose': 'Ara',
      'xylose': 'Xyl', 'ribose': 'Rib', 'lyxose': 'Lyx',
      'glucuronic': 'GlcA', 'glucuronicacid': 'GlcA',
      'galacturonic': 'GalA', 'galacturonicacid': 'GalA',
      'mannuronic': 'ManA', 'mannuronicacid': 'ManA',
      'iduronic': 'IdoA', 'iduronicacid': 'IdoA',
      'glucosamine': 'GlcN', 'galactosamine': 'GalN', 'mannosamine': 'ManN',
      'nacetylglucosamine': 'GlcNAc', 'nacetylgalactosamine': 'GalNAc',
      'nacetylmannosamine': 'ManNAc', 'neuraminic': 'Neu',
      'neuraminicacid': 'Neu', 'sialic': 'Neu', 'sialicacid': 'Neu',
      'fructose': 'Fru', 'kdo': 'Kdo', 'heptose': 'Hep'
    };

    let normalized = queryString
      .trim()
      .replace(/→/g, '-')
      .replace(/->/g, '-')
      .replace(/\s+/g, '')
      .replace(/[–—]/g, '-')
      .replace(/α/g, 'a')
      .replace(/β/g, 'b')
      .replace(/alpha/gi, 'a')  // Normalize "alpha" → "a"
      .replace(/beta/gi, 'b')   // Normalize "beta" → "b"
      .replace(/[()]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    // Convert full sugar words to abbreviations
    normalized = normalized.split('-').map(part => {
      const lowerPart = part.toLowerCase();

      // Check if whole part matches a sugar word
      if (SUGAR_WORD_MAP[lowerPart]) {
        return SUGAR_WORD_MAP[lowerPart];
      }

      // Try stereochemistry prefixes: [ab][DL]sugarword
      const stereoMatch = lowerPart.match(/^([ab])([dl])(.*)/);
      if (stereoMatch && SUGAR_WORD_MAP[stereoMatch[3]]) {
        return stereoMatch[1] + stereoMatch[2].toUpperCase() + SUGAR_WORD_MAP[stereoMatch[3]];
      }

      // Try [ab] prefix: "agalactose" -> "aGal"
      const alphaMatch = lowerPart.match(/^([ab])(.*)/);
      if (alphaMatch && SUGAR_WORD_MAP[alphaMatch[2]]) {
        return alphaMatch[1] + SUGAR_WORD_MAP[alphaMatch[2]];
      }

      // Try [DL] prefix: "dglucose" -> "DGlc"
      const dlMatch = lowerPart.match(/^([dl])(.*)/);
      if (dlMatch && SUGAR_WORD_MAP[dlMatch[2]]) {
        return dlMatch[1].toUpperCase() + SUGAR_WORD_MAP[dlMatch[2]];
      }

      return part;
    }).join('-');

    // Capitalize sugar names
    normalized = normalized.split('-').map(part => {
      if (part === '*') return part;
      if (!part) return part;

      // Check if already properly formatted
      const hasUpperCase = /[A-Z]/.test(part);
      const hasLowerCase = /[a-z]/.test(part);
      if (hasUpperCase && hasLowerCase && part.length >= 3) {
        return part;
      }

      // Pattern: optional [ab], optional [DL], then sugar name
      const match = part.match(/^([ab])?([DL])?(.+)$/i);
      if (match) {
        const alphaBeta = match[1] ? match[1].toLowerCase() : '';
        const DL = match[2] ? match[2].toUpperCase() : '';
        const sugarName = match[3];

        let capitalizedSugar = sugarName;
        const isAllLowercase = sugarName === sugarName.toLowerCase();
        const isAllUppercase = sugarName === sugarName.toUpperCase();

        if (isAllLowercase || isAllUppercase) {
          capitalizedSugar = sugarName.charAt(0).toUpperCase() + sugarName.slice(1).toLowerCase();
        }

        return alphaBeta + DL + capitalizedSugar;
      }

      return part;
    }).join('-');

    return normalized;
  };

  // Helper: Check if a token is a bond notation
  const isBondNotation = (token) => {
    // Pattern: b4, a3, 4, (1->4), etc.
    return /^([ab])?\d$/.test(token) || /^\(\d+->\d+\)$/.test(token);
  };

  // Helper: Parse bond notation string
  const parseBondNotation = (token) => {
    // Shorthand: "b4", "a3"
    const shortMatch = token.match(/^([ab])(\d)$/);
    if (shortMatch) {
      return {
        anomeric: shortMatch[1],
        from: 1,
        to: parseInt(shortMatch[2], 10)
      };
    }

    // Just position: "4", "3"
    const digitMatch = token.match(/^(\d)$/);
    if (digitMatch) {
      return {
        anomeric: null,
        from: 1,
        to: parseInt(digitMatch[1], 10)
      };
    }

    return null;
  };

  // Convert query string to pattern object (with bonds and branches)
  const queryToPattern = (queryString) => {
    if (!queryString || !queryString.trim()) {
      return { mainChain: [], branches: [], bonds: [] };
    }

    // Normalize the query first (convert "glucose" -> "Glc", "alpha" -> "a", etc.)
    const normalized = normalizeQueryString(queryString);

    // Extract branches using bracket notation
    const { mainChainString, branchSpecs } = extractBranchesFromQuery(normalized);

    // Parse the main chain
    const mainChainResult = parseLinearChainString(mainChainString);

    // Parse each branch
    const parsedBranches = [];
    for (const branchSpec of branchSpecs) {
      const branchResult = parseBranchFromSpec(branchSpec, mainChainResult.sugars);
      if (branchResult) {
        parsedBranches.push(branchResult);
      }
    }

    return {
      mainChain: mainChainResult.sugars,
      branches: parsedBranches,
      bonds: mainChainResult.bonds
    };
  };

  // Extract branches from query string (frontend version of backend logic)
  const extractBranchesFromQuery = (queryString) => {
    const branchSpecs = [];
    let mainChainString = '';
    let currentSugarIndex = -1;
    let i = 0;
    let inSugar = false;
    let inBond = false;

    while (i < queryString.length) {
      const char = queryString[i];

      if (char === '[') {
        // Start of branch - find matching closing bracket
        let depth = 1;
        let j = i + 1;
        while (j < queryString.length && depth > 0) {
          if (queryString[j] === '[') depth++;
          if (queryString[j] === ']') depth--;
          j++;
        }

        if (depth === 0) {
          // Found matching bracket - attach to current sugar
          const branchContent = queryString.substring(i + 1, j - 1);
          branchSpecs.push({
            attachToPosition: currentSugarIndex,
            content: branchContent
          });
          i = j; // Skip past the branch
        } else {
          // Unmatched bracket - treat as regular character
          mainChainString += char;
          i++;
        }
      } else {
        // Regular character - add to main chain
        mainChainString += char;

        // Detect if we're starting a new sugar
        if (char === '-') {
          inSugar = false;
          inBond = false;
        } else if (/[a-zA-Z*]/.test(char) && !inSugar && !inBond) {
          // Starting a new token - check if it's a bond notation or sugar
          const remaining = queryString.substring(i);
          const bondMatch = remaining.match(/^([ab]?\d)/);

          if (bondMatch) {
            // This is a bond notation
            inBond = true;
          } else {
            // This is a sugar or wildcard
            inSugar = true;
            currentSugarIndex++;
          }
        }

        i++;
      }
    }

    return {
      mainChainString: mainChainString,
      branchSpecs: branchSpecs
    };
  };

  // Parse a linear chain string (no brackets)
  const parseLinearChainString = (chainString) => {
    const tokens = chainString.split('-').filter(t => t.length > 0);
    const sugars = [];
    const bonds = [];
    let sugarPosition = 0;

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i].trim();

      // Check if this is a bond notation
      if (isBondNotation(token)) {
        const bondInfo = parseBondNotation(token);
        if (bondInfo && bonds.length > 0) {
          // Update the previous bond
          bonds[bonds.length - 1] = bondInfo;
        }
        continue;
      }

      // This is a sugar or wildcard
      if (token === '*') {
        sugars.push({
          id: `wildcard-${Date.now()}-${sugarPosition}`,
          sugar: '*',
          baseSugar: '*',
          isWildcard: true,
          position: sugarPosition
        });
        bonds.push(null); // Add placeholder for bond after this sugar
        sugarPosition++;
        continue;
      }

      // Try to find sugar in library
      const sugarData = resdict[token];
      if (sugarData) {
        sugars.push({
          id: `${token}-${Date.now()}-${sugarPosition}`,
          sugar: token,
          baseSugar: token,
          shape: sugarData.shape,
          color: sugarData.colour,
          isWildcard: false,
          isUnknown: false,
          position: sugarPosition
        });
        bonds.push(null); // Add placeholder for bond after this sugar
        sugarPosition++;
        continue;
      }

      // If not found, mark as unknown (will display with warning)
      sugars.push({
        id: `unknown-${Date.now()}-${sugarPosition}`,
        sugar: token,
        baseSugar: token,
        shape: 'filled-circle',
        color: 'white',
        isWildcard: false,
        isUnknown: true,
        position: sugarPosition
      });
      bonds.push(null); // Add placeholder for bond after this sugar
      sugarPosition++;
    }

    return { sugars, bonds };
  };

  // Parse a branch specification
  const parseBranchFromSpec = (branchSpec, mainChainSugars) => {
    const { attachToPosition, content } = branchSpec;

    // Validate attachment position (but allow -1 for standalone branch-only queries)
    // When attachToPosition is -1 and mainChain is empty, this is a branch-only query
    const isBranchOnlyQuery = mainChainSugars.length === 0 && attachToPosition === -1;

    if (!isBranchOnlyQuery && (attachToPosition < 0 || attachToPosition >= mainChainSugars.length)) {
      console.warn(`Invalid branch attachment position ${attachToPosition}`);
      return null;
    }

    // Parse the branch content as a linear chain
    const branchResult = parseLinearChainString(content);

    if (branchResult.sugars.length === 0) {
      console.warn('Branch has no sugars');
      return null;
    }

    // The LAST bond in the branch is the attachment linkage
    let attachmentBond = null;
    let branchBonds = branchResult.bonds;

    if (branchBonds.length > 0) {
      attachmentBond = branchBonds[branchBonds.length - 1];
      branchBonds = branchBonds.slice(0, -1);
    }

    return {
      id: `branch-${Date.now()}-${attachToPosition}`,
      attachToPosition: isBranchOnlyQuery ? 0 : attachToPosition, // Default to 0 for standalone
      sugars: branchResult.sugars,
      bonds: branchBonds,
      attachmentBond: attachmentBond
    };
  };

  // Add sugar to main chain
  const addSugarToMainChain = (sugarName) => {
    const sugarData = resdict[sugarName];
    if (!sugarData) return;

    const newSugar = {
      id: `${sugarName}-${Date.now()}`,
      sugar: sugarName,
      baseSugar: sugarName,
      shape: sugarData.shape,
      color: sugarData.colour,
      isWildcard: false,
      position: pattern.mainChain.length
    };

    setPattern(prev => ({
      ...prev,
      mainChain: [...prev.mainChain, newSugar],
      bonds: [...(prev.bonds || []), null] // Add null bond placeholder
    }));
  };

  // Add wildcard to main chain
  const addWildcard = () => {
    const newWildcard = {
      id: `wildcard-${Date.now()}`,
      sugar: '*',
      baseSugar: '*',
      isWildcard: true,
      position: pattern.mainChain.length
    };

    setPattern(prev => ({
      ...prev,
      mainChain: [...prev.mainChain, newWildcard],
      bonds: [...(prev.bonds || []), null] // Add null bond placeholder
    }));
  };

  // Remove sugar from main chain
  const removeSugarFromMainChain = (sugarId) => {
    const sugarIndex = pattern.mainChain.findIndex(s => s.id === sugarId);

    setPattern(prev => ({
      ...prev,
      mainChain: prev.mainChain.filter(s => s.id !== sugarId),
      bonds: prev.bonds ? prev.bonds.filter((_, idx) => idx !== sugarIndex) : []
    }));
  };

  // Update bond for a specific sugar
  const updateBond = (sugarIndex, bondInfo) => {
    setPattern(prev => {
      const newBonds = [...(prev.bonds || [])];
      newBonds[sugarIndex] = bondInfo;
      return {
        ...prev,
        bonds: newBonds
      };
    });
  };

  // Clear entire pattern
  const clearPattern = () => {
    setPattern({ mainChain: [], branches: [], bonds: [] });
    setTextQuery('');
  };

  // Handle text query input change
  const handleTextQueryChange = (e) => {
    const newQuery = e.target.value;
    setTextQuery(newQuery);

    // Check if this is a multi-module query
    const isMulti = isMultiModuleQuery(newQuery);
    setIsMultiModule(isMulti);

    if (isMulti) {
      // Multi-module query: parse tokens for preview
      const tokens = parseMultiModuleQuery(newQuery);
      setTokenPreview(tokens);
    } else {
      // Single-module query: parse as carbuilder structure
      setTokenPreview([]);
      try {
        const parsedPattern = queryToPattern(newQuery);
        setPattern(parsedPattern);
      } catch (error) {
        console.error('Error parsing query:', error);
      }
    }
  };

  // Execute search
  const handleSearch = () => {
    // For multi-module queries, use the textQuery directly
    const query = isMultiModule ? textQuery : patternToQuery(pattern);

    if (!query || query.trim() === '') {
      alert('Please build a structure or enter a search query');
      return;
    }

    // Pass the query WITH brackets to handleAdvancedSearch
    // The brackets will be stripped there before sending to backend,
    // but kept in the search bar for UI consistency and auto-detection
    onSearch(query, config, searchType);
  };

  // Tab panes for the modal
  const panes = [
    {
      menuItem: { key: 'builder', icon: 'cubes', content: 'Visual Builder' },
      render: () => (
        <Tab.Pane className="pattern-builder-pane">
          {isMultiModule ? (
            <div style={{
              padding: '40px',
              textAlign: 'center',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              minHeight: '400px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '16px'
            }}>
              <Icon name="info circle" size="huge" color="blue" />
              <h2 style={{ margin: '16px 0 8px 0' }}>Multi-Module Query Detected</h2>
              <p style={{ fontSize: '1.1em', color: '#555', maxWidth: '500px', margin: '0 0 16px 0' }}>
                The visual builder is designed for structure-only queries. Your query includes multiple search types:
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', margin: '16px 0' }}>
                {tokenPreview.map((item, index) => (
                  <Label
                    key={index}
                    size="large"
                    style={{
                      backgroundColor: getModuleColor(item.module),
                      color: item.module === 'species' ? '#333' : '#fff',
                      border: 'none'
                    }}
                  >
                    <Icon name={getModuleIcon(item.module)} />
                    {item.token}
                    <Label.Detail style={{ backgroundColor: 'rgba(0,0,0,0.15)' }}>
                      {item.module}
                    </Label.Detail>
                  </Label>
                ))}
              </div>
              <div style={{
                backgroundColor: '#fff',
                padding: '16px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                maxWidth: '500px',
                textAlign: 'left'
              }}>
                <div style={{ marginBottom: '12px' }}>
                  <Icon name="arrow right" color="blue" />
                  <strong>Use the "Text Query" tab</strong> to search with this multi-module query
                </div>
                <div>
                  <Icon name="lightbulb outline" color="yellow" />
                  <strong>Tip:</strong> Multi-module queries search across all fields with AND logic, returning only entries that match all criteria
                </div>
              </div>
              <Button
                primary
                size="large"
                onClick={() => setActiveTab(1)}
                style={{ marginTop: '16px' }}
              >
                <Icon name="arrow right" />
                Go to Text Query Tab
              </Button>
            </div>
          ) : (
            <div className="pattern-builder-section">
              {/* Main Chain Builder - Disabled when scope is 'branches' */}
              {config.carbuilder.searchScope !== 'branches' && (
                <>
                  <PatternCanvas
                    pattern={pattern}
                    onRemoveSugar={removeSugarFromMainChain}
                    onClearPattern={clearPattern}
                    onUpdateBond={updateBond}
                  />

                  <div className="palette-section">
                    <h3>
                      <Icon name="grid layout" />
                      Sugar Library
                    </h3>
                    <SugarPalette
                      onSelectSugar={addSugarToMainChain}
                      onAddWildcard={addWildcard}
                    />
                  </div>
                </>
              )}

              {/* Branches-Only Mode UI */}
              {config.carbuilder.searchScope === 'branches' && (
                <div style={{ padding: '20px' }}>
                  <div style={{
                    background: '#f0f8ff',
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    border: '1px solid #b8daff'
                  }}>
                    <Icon name="info circle" color="blue" />
                    <strong>Branch Pattern Mode</strong>
                    <p style={{ margin: '8px 0 0 0', color: '#555', fontSize: '0.95em' }}>
                      Building standalone branch patterns. Your query will search for these patterns within any branch in the structure.
                    </p>
                  </div>

                  {showBranchBuilder && (
                    <BranchBuilder
                      pattern={pattern}
                      setPattern={setPattern}
                      onClose={() => setShowBranchBuilder(false)}
                      mode="standalone"
                    />
                  )}

                  {!showBranchBuilder && (
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                      {pattern.branches && pattern.branches.length > 0 ? (
                        <div>
                          <div style={{
                            background: '#f8f9fa',
                            padding: '20px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            border: '2px solid #28a745'
                          }}>
                            <h4 style={{ margin: '0 0 15px 0' }}>
                              <Icon name="check circle" color="green" />
                              Branch Pattern Built
                            </h4>
                            {/* Visual SNFG preview */}
                            <div className="branch-chain" style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginBottom: '10px'
                            }}>
                              {pattern.branches[0].sugars.map((sugar, sugarIndex) => (
                                <div key={sugar.id} className="sugar-in-chain" style={{
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  <div className="sugar-item small">
                                    <div className="sugar-shape small">
                                      <SNFGsvg snfg={sugar.sugar} />
                                    </div>
                                    <div className="sugar-name small">{sugar.sugar}</div>
                                  </div>
                                  {sugarIndex < pattern.branches[0].sugars.length - 1 && (
                                    <div className="sugar-connector small">
                                      {pattern.branches[0].bonds && pattern.branches[0].bonds[sugarIndex] ? (
                                        <span className="bond-label-small">
                                          {pattern.branches[0].bonds[sugarIndex].anomeric || ''}
                                          {pattern.branches[0].bonds[sugarIndex].from || 1}→{pattern.branches[0].bonds[sugarIndex].to}
                                        </span>
                                      ) : (
                                        <Icon name="minus" />
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                            {/* Text query below */}
                            <div style={{
                              fontSize: '0.9em',
                              fontFamily: 'monospace',
                              color: '#666',
                              marginTop: '10px'
                            }}>
                              {branchToQuery(pattern.branches[0])}
                            </div>
                          </div>
                          <Button
                            onClick={() => setShowBranchBuilder(true)}
                            basic
                          >
                            <Icon name="edit" />
                            Edit Pattern
                          </Button>
                          <Button
                            onClick={clearPattern}
                            basic
                            style={{ marginLeft: '10px' }}
                          >
                            <Icon name="redo" />
                            Clear Pattern
                          </Button>
                        </div>
                      ) : (
                        <>
                          <Button
                            onClick={() => setShowBranchBuilder(true)}
                            size="large"
                            primary
                            className="add-branch-button"
                          >
                            <Icon name="fork" />
                            Build Branch Pattern
                          </Button>
                          <p style={{ marginTop: '15px', color: '#666', fontSize: '0.9em' }}>
                            Examples: <code>GlcA</code>, <code>Glc-Man</code>
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Branch Builder for Anywhere Mode */}
              {config.carbuilder.searchScope === 'anywhere' && (
                <>
                  {showBranchBuilder && (
                    <BranchBuilder
                      pattern={pattern}
                      setPattern={setPattern}
                      onClose={() => setShowBranchBuilder(false)}
                      mode="attached"
                    />
                  )}

                  {!showBranchBuilder && pattern.mainChain.length > 0 && (
                    <Button
                      onClick={() => setShowBranchBuilder(true)}
                      className="add-branch-button"
                    >
                      <Icon name="fork" />
                      Add Branch
                    </Button>
                  )}
                </>
              )}

              {/* Show message when branches are locked in Main Chain mode */}
              {config.carbuilder.searchScope === 'mainChain' && pattern.mainChain.length > 0 && (
                <div style={{ padding: '10px', marginTop: '10px', textAlign: 'center', color: '#999', fontSize: '0.9em' }}>
                  <Icon name="info circle" />
                  Branches disabled in "Main Chain Only" scope. Change to "Anywhere" to add branches.
                </div>
              )}
            </div>
          )}
        </Tab.Pane>
      )
    },
    {
      menuItem: { key: 'text', icon: 'keyboard', content: 'Text Query' },
      render: () => (
        <Tab.Pane className="text-query-pane">
          <h3>
            <Icon name="edit" />
            Enter Query as Text
          </h3>
          <div className="text-query-input-section">
            <textarea
              value={textQuery}
              onChange={handleTextQueryChange}
              placeholder="Enter structure like: Glc-Man-Gal, Glc-b4-Man-a3-Gal, or Glc-Man[GlcA-a3]-Glc (with branch)"
              rows={4}
              className="text-query-textarea"
            />

            {isMultiModule && tokenPreview.length > 0 && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: '#e8f5e9',
                border: '1px solid #4caf50',
                borderRadius: '4px'
              }}>
                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon name="check circle" color="green" />
                  <strong>Multi-Module Query Detected</strong>
                </div>
                <div style={{ marginBottom: '8px', fontSize: '0.9em', color: '#2e7d32' }}>
                  This query searches across multiple modules with AND logic. Detected modules:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                  {tokenPreview.map((item, index) => (
                    <Label
                      key={index}
                      size="small"
                      style={{
                        backgroundColor: getModuleColor(item.module),
                        color: item.module === 'species' ? '#333' : '#fff',
                        border: 'none'
                      }}
                    >
                      <Icon name={getModuleIcon(item.module)} />
                      {item.token}
                      <Label.Detail style={{ backgroundColor: 'rgba(0,0,0,0.15)' }}>
                        {item.module}
                      </Label.Detail>
                    </Label>
                  ))}
                </div>
                <div style={{ marginTop: '8px', fontSize: '0.85em', color: '#555' }}>
                  <Icon name="lightbulb outline" />
                  Results will match ALL criteria (AND logic). Use the search bar for quick multi-module searches.
                </div>
              </div>
            )}

            <div className="query-help">
              <Icon name="info circle" />
              <div>
                <strong>Syntax:</strong>
                <ul>
                  <li>Basic: <code>Glc-Man-Gal</code></li>
                  <li>With bonds: <code>Glc-b4-Man-a3-Gal</code> (β1→4, α1→3)</li>
                  <li>With branches: <code>Glc-b4-Man[GlcA-a3]-b4-Glc</code> (GlcA branch via α1→3 on Man)</li>
                  <li>Wildcards: <code>Glc-*-Man</code> or <code>*-b4-*</code></li>
                  <li>Bond formats: <code>b4</code> (β1→4), <code>a3</code> (α1→3), <code>4</code> (any→4)</li>
                  <li>Multi-module: <code>O8Ec Glc-Man</code> or <code>"Escherichia coli" O157</code></li>
                </ul>
              </div>
            </div>
          </div>
        </Tab.Pane>
      )
    }
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="fullscreen"
      className="advanced-search-modal"
      closeIcon
    >
      <Modal.Header>
        <Icon name="search plus" />
        Advanced Structure Search
      </Modal.Header>

      <Modal.Content scrolling>
        <div className="modal-layout">
          {/* Left Panel: Pattern Building */}
          <div className="pattern-builder-panel">
            <Tab
              panes={panes}
              activeIndex={activeTab}
              onTabChange={(e, { activeIndex }) => setActiveTab(activeIndex)}
            />
          </div>

          {/* Right Panel: Configuration */}
          <div className="configuration-panel">
            <SearchConfiguration
              config={config}
              setConfig={setConfig}
              pattern={pattern}
              setPattern={setPattern}
            />
          </div>
        </div>
      </Modal.Content>

      <Modal.Actions>
        <div className="modal-actions-content">
          <div className="pattern-summary">
            <div style={{ marginRight: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <strong>Search Type:</strong>
              <Dropdown
                selection
                compact
                value={searchType}
                options={[
                  { key: 'all', value: 'all', text: 'All Fields', icon: 'globe' },
                  { key: 'pattern', value: 'pattern', text: 'Structure', icon: 'connectdevelop' },
                  { key: 'serotype', value: 'serotype', text: 'Serotype', icon: 'tag' },
                  { key: 'nmr', value: 'nmr', text: 'NMR', icon: 'chart line' },
                  { key: 'species', value: 'species', text: 'Species', icon: 'dna' }
                ]}
                onChange={(e, { value }) => setSearchType(value)}
                style={{ minWidth: '140px' }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isMultiModule ? (
                <>
                  <Icon name="globe" />
                  <strong>Multi-Module Query:</strong> {textQuery}
                  <span className="sugar-count">
                    ({tokenPreview.length} tokens, AND logic)
                  </span>
                </>
              ) : pattern.mainChain.length > 0 ? (
                <>
                  <Icon name="chain" />
                  <strong>Structure:</strong> {textQuery}
                  {pattern.mainChain.length > 1 && (
                    <span className="sugar-count">
                      ({pattern.mainChain.length} sugars)
                    </span>
                  )}
                </>
              ) : (
                <span className="no-pattern">
                  <Icon name="warning sign" color="orange" />
                  No structure built yet
                </span>
              )}
            </div>
          </div>

          <div className="action-buttons">
            <Button onClick={clearPattern} basic>
              <Icon name="redo" />
              Clear Structure
            </Button>

            <Button onClick={onClose} basic>
              <Icon name="close" />
              Cancel
            </Button>

            <Button
              onClick={handleSearch}
              primary
              disabled={!isMultiModule && pattern.mainChain.length === 0 && pattern.branches.length === 0}
            >
              <Icon name="search" />
              {isMultiModule ? 'Search All Fields' : 'Search Structure'}
            </Button>
          </div>
        </div>
      </Modal.Actions>
    </Modal>
  );
}
